import { useEffect, useMemo, useRef } from 'react';
import type { OutboxEntry } from '@/app/outbox-types';

/**
 * A fila que vai por cima de uma leitura (Fase 43, ADR-195).
 *
 * O que subiu continua por cima até a leitura ser refeita: a fila do aparelho responde antes da
 * rede, e sem isto o cartão confirmado piscaria "Pendente" entre a operação sair da fila e a
 * leitura nova chegar. Quando a leitura muda — chegou a do servidor —, sobra só o que ainda
 * está na fila.
 */
export function useFilaPorCima(
  fila: readonly OutboxEntry[] | undefined,
  leitura: unknown,
): readonly OutboxEntry[] {
  const daLeitura = useRef<{ leitura: unknown; entries: readonly OutboxEntry[] }>({
    leitura: undefined,
    entries: [],
  });
  const entradas = useMemo(() => {
    const atuais = fila ?? [];
    const antes = daLeitura.current;
    if (leitura === undefined || antes.leitura !== leitura) return atuais;
    const ainda = new Set(atuais.map((entrada) => entrada.id));
    return [...antes.entries.filter((entrada) => !ainda.has(entrada.id)), ...atuais];
  }, [fila, leitura]);
  useEffect(() => {
    daLeitura.current = { leitura, entries: entradas };
  }, [leitura, entradas]);
  return entradas;
}
